import clsx from "clsx";
import React, { FC, useState } from "react";
import { Box, InputAdornment, TextField } from "@mui/material";
import { floatNumberInputProps } from "../../../constants";
import makeStyles from "@mui/styles/makeStyles";
import { colorSystem } from "../../../theme";
import { CustomMaskRenderArgs, measureNumberWidth } from "./customAdornments";
import { thousandSeparator } from "../utils/thousandSeparator";

const useStyles = makeStyles((theme) => ({
  input: {
    "& input": {
      fontWeight: 500,
    },
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
  baldValue: {
    "& input": {
      fontWeight: 700,
    },
    fontWeight: 700,
  },
  gramContainer: {
    position: "relative",
  },
  gram: {
    position: "absolute",
    zIndex: 2,
    top: "52%",
    transform: "translateY(-52%)",
    fontSize: 16,
  },
  target: {
    position: "absolute",
    zIndex: 2,
    top: "52%",
    transform: "translateY(-52%)",
    fontSize: 12,
    color: colorSystem.gray3,
  },
  zeroColor: {
    color: colorSystem.gray4,
  },
  protein: {
    color: colorSystem.nutritionProteins,
  },

  carbohydrate: {
    color: colorSystem.nutritionCarbs,
  },

  fat: {
    color: colorSystem.nutritionFats,
  },

  adromentContent: {
    fontWeight: 600,
  },
}));

const gramRender = (renderArgs: CustomMaskRenderArgs, units: string) => {
  const { value, className, font, onClick } = renderArgs;
  const width = measureNumberWidth(value, font);
  return (
    <span style={{ left: width + 19 }} className={className} onClick={onClick}>
      {units}
    </span>
  );
};

const zeroRender = (renderArgs: CustomMaskRenderArgs, placeholder?: number) => {
  const { value, className, font, onClick } = renderArgs;
  const width = measureNumberWidth(value, font);
  return (
    <span style={{ left: width }} className={className} onClick={onClick}>
      {placeholder || 0}
    </span>
  );
};

const targetRender = (
  renderArgs: CustomMaskRenderArgs,
  targetValue: string,
  units: string,
  dividerSize: number,
  noMainGramRender: boolean,
) => {
  const { value, className, font, onClick } = renderArgs;
  const valueWidth = measureNumberWidth(value, font);
  const unitsWidth = noMainGramRender ? -6 : measureNumberWidth(units, font);
  const dividerWidth = measureNumberWidth(
    "/",
    `500 ${dividerSize}px Montserrat`,
  );
  return (
    <>
      <span
        style={{ left: valueWidth + unitsWidth + 30, fontSize: dividerSize }}
        className={className}
        onClick={onClick}
      >
        {"/"}
      </span>
      <span
        style={{ left: valueWidth + unitsWidth + dividerWidth + 27 }}
        className={className}
        onClick={onClick}
      >
        {targetValue} {units}
      </span>
    </>
  );
};

export interface NutrientValueProps {
  value: number;
  onChange: (value: number) => void;
  dirty: boolean;
  targetValue?: number;
}

export interface NutrientInputProps {
  nutrient: NutrientValueProps;
  type: "protein" | "carbohydrate" | "fat" | "calories";
  exampleValue: number;
  adornmentProps: {
    variant: "none" | "distribution" | "target-overflow";
    value?: number;
    targetValue?: number;
    percent?: number;
  };
  displayError?: boolean;
}

const NutrientInput: FC<NutrientInputProps> = ({
  nutrient,
  type,
  exampleValue,
  adornmentProps,
  displayError = false,
}) => {
  const s = useStyles();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState(false);

  const imperativeSetFocus = () => {
    if (inputRef.current && !focus) {
      inputRef.current.focus();
    }
  };

  const units = type === "calories" ? "kcal" : "g";

  const baldValue = type === "calories";

  const isEmpty = (value: number) => value === 0 || value === null;
  const renderZero = isEmpty(nutrient.value) && !focus;

  const addBaldStyle = baldValue ? [s.baldValue] : [];

  const hasTargetValue =
    nutrient.targetValue !== undefined && nutrient.targetValue !== null;

  const hasTargetInAdornment =
    adornmentProps.variant === "target-overflow" && adornmentProps.targetValue;

  const omitMainGramRender = type === "calories" && hasTargetValue;

  let AdornmentContent: React.ReactNode | undefined = undefined;

  if (adornmentProps.variant === "distribution" && adornmentProps.percent > 0) {
    AdornmentContent = (
      <span className={s.adromentContent}>{`${adornmentProps.percent}%`}</span>
    );
  } else if (hasTargetInAdornment) {
    const left = adornmentProps.targetValue - adornmentProps.value;
    const differenceFormatted = thousandSeparator(Math.round(Math.abs(left)));

    AdornmentContent = (
      <span className={s.adromentContent}>
        {`${differenceFormatted} ${type === "calories" ? "kcal" : "g"} ` +
          `${left >= 0 ? "left" : "over"}`}
      </span>
    );
  }

  return (
    <Box className={s.gramContainer}>
      <TextField
        inputRef={inputRef}
        variant="outlined"
        fullWidth
        autoComplete="off"
        size="small"
        value={isEmpty(nutrient.value) ? "" : Math.round(nutrient.value)}
        onChange={(e) => {
          nutrient.onChange(parseFloat(e.target.value));
        }}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        className={baldValue ? clsx(s.input, s.baldValue) : s.input}
        error={
          (!nutrient.value || nutrient.value <= 0) &&
          nutrient.dirty &&
          displayError
        }
        slotProps={{
          input: {
            inputProps: floatNumberInputProps,
            endAdornment: (
              <InputAdornment position="end" className={s?.[type]}>
                {AdornmentContent}
              </InputAdornment>
            ),
          },
        }}
      />
      {!omitMainGramRender &&
        gramRender(
          {
            value: renderZero
              ? Math.round(exampleValue).toString()
              : Math.round(nutrient.value).toString(),
            className: renderZero
              ? clsx(s.gram, s.zeroColor, ...addBaldStyle)
              : clsx(s.gram, ...addBaldStyle),
            font: `${baldValue ? 700 : 500} 16px Montserrat`,
            onClick: imperativeSetFocus,
          },

          units,
        )}
      {renderZero &&
        zeroRender(
          {
            value: Math.round(nutrient.value).toString(),
            className: clsx(s.gram, s.zeroColor, ...addBaldStyle),
            font: `${baldValue ? 700 : 500} 16px Montserrat`,
            onClick: imperativeSetFocus,
          },
          exampleValue,
        )}
      {hasTargetValue &&
        targetRender(
          {
            value: Math.round(nutrient.value).toString(),
            className: s.target,
            font: `${baldValue ? 700 : 500} 16px Montserrat`,
            onClick: imperativeSetFocus,
          },
          thousandSeparator(Math.round(nutrient.targetValue)).toString(),
          units,
          16,
          omitMainGramRender,
        )}
    </Box>
  );
};

export default NutrientInput;
